import { AnimatePresence, motion } from 'framer-motion';
import { MessageSquare, TrendingUp, Trophy, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import homepageImg from '../../assets/screens/homepage.png';
import profile3Img from '../../assets/screens/profile-3.png';
import profileDetailsImg from '../../assets/screens/profile.png';
import tipsImg from '../../assets/screens/tips.png';
import tribesImg from '../../assets/screens/tribes.png';
import appStoreBadge from '../../assets/stores/appstore.png';
import playStoreBadge from '../../assets/stores/playstore.png';

const HeroSection = () => {
  const [activeScreen, setActiveScreen] = useState(0);
  const APP_STORE_URL = "https://apps.apple.com/us/app/wealthdost/id6758073703";
  const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.fortybears.wealthdost";

  const heroScreens = [
    { title: 'Homepage', src: homepageImg },
    { title: 'Tribes', src: tribesImg },
    { title: 'Tips', src: tipsImg },
    { title: 'Profile', src: profile3Img },
    { title: 'Profile Details', src: profileDetailsImg }
  ];

  const screenCallouts = [
    {
      left: { text: "Verified Expert Insights", icon: TrendingUp },
      right: { text: "Track Record Visible", icon: Trophy }
    },
    {
      left: { text: "Join Investment Tribes", icon: Users },
      right: { text: "Community Opinions", icon: MessageSquare }
    },
    {
      left: { text: "Actionable Market Tips", icon: TrendingUp },
      right: { text: "Real-time Discussions", icon: MessageSquare }
    },
    {
      left: { text: "Expert Profiles", icon: TrendingUp },
      right: { text: "Performance Rankings", icon: Trophy }
    },
    {
      left: { text: "Transparent Track Records", icon: Trophy },
      right: { text: "Like-minded Investors", icon: Users }
    }
  ];

  // Auto-rotate screens
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveScreen(prev => (prev + 1) % heroScreens.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [heroScreens.length]);

  return (
    <section className="relative min-h-screen pt-20 md:pt-0 flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background glow */}
      <div className="hero-glow absolute top-1/4 -left-40 opacity-50" />
      <div className="hero-glow absolute bottom-1/4 -right-40 opacity-30" />
      
      <div className="max-w-7xl mx-auto px-6 w-full">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16 py-12 lg:py-0">
          {/* Left Content */}
          <motion.div 
            className="flex-1 text-left"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F8FAFC] leading-tight mb-6">
              Bharat's{' '}
              <span className="gradient-text">Trust-First</span>
              <br />
              Finance Community
            </h1>

            <div className="space-y-3 mb-8 text-base md:text-lg text-[#94A3B8]">
              <p>Learn from credible experts.</p>
              <p>Discuss stocks with serious investors.</p>
              <p>Cut through financial noise.</p>
            </div>

            <div className="flex flex-row gap-4">
              <motion.a
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center"
                data-testid="hero-cta-primary"
                aria-label="Download on the App Store"
              >
                <img src={appStoreBadge} alt="App Store" className="h-12 w-auto" />
              </motion.a>

              <motion.a
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center"
                data-testid="hero-cta-secondary"
                aria-label="Get it on Google Play"
              >
                <img src={playStoreBadge} alt="Google Play" className="h-12 w-auto" />
              </motion.a>
            </div>
          </motion.div>

          {/* Right - App Screens */}
          <motion.div 
            className="flex-1 flex justify-center lg:justify-end"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="relative">
              {/* Left Callouts */}
              <div className="absolute left-[-200px] top-[110px] hidden flex-col gap-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`left-${activeScreen}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4 }}
                    className="flex items-center gap-3"
                  >
                    <div className="text-right">
                      <p className="text-[#F8FAFC] text-sm font-medium">{screenCallouts[activeScreen].left.text}</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-[#2563EB]/20 flex items-center justify-center">
                      {React.createElement(screenCallouts[activeScreen].left.icon, { size: 18, className: "text-[#22D3EE]" })}
                    </div>
                    <div className="w-12 h-[2px] bg-gradient-to-r from-[#22D3EE] to-transparent" />
                  </motion.div>
                </AnimatePresence>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={`right-${activeScreen}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4 }}
                    className="flex items-center gap-3"
                  >
                    <div className="text-right">
                      <p className="text-[#F8FAFC] text-sm font-medium">{screenCallouts[activeScreen].right.text}</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                      {React.createElement(screenCallouts[activeScreen].right.icon, { size: 18, className: "text-[#10B981]" })}
                    </div>
                    <div className="w-12 h-[2px] bg-gradient-to-r from-[#10B981] to-transparent" />
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="w-[280px] sm:w-[320px] md:w-[360px] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.img
                  key={heroScreens[activeScreen].title}
                  src={heroScreens[activeScreen].src}
                  alt={`${heroScreens[activeScreen].title} screenshot`}
                  className="w-full h-auto object-contain"
                  initial={{ opacity: 0, y: 60 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -60 }}
                  transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
                />
              </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
